// -----------------------------------------------------------------------------
// Per-route SEO metadata — the curated, API-INDEPENDENT source of truth for the
// 252 public URLs in public/sitemap.xml.
//
// WHY THIS EXISTS
// Titles/descriptions used to be derived from live API responses (a state's
// `metadata` blob, a document's `seoData`, a hardcoded getSEOdata() table).
// That made the head non-deterministic: when the API was slow, partial or
// missing a field, pages shipped a generic description — or, worse, a canonical
// of https://www.jaaga.ai/ on EVERY document page, which collapses 244 URLs
// into one in Google's index.
//
// seo-metadata.json is hand-written, keyword-mapped, and verified to match
// public/sitemap.xml exactly (same 252 paths, same absolute https/www
// canonicals, no trailing slashes) — so a canonical and its sitemap entry can
// never disagree. Because it needs no network, it is also what lets the build
// prerender these routes with real head tags.
//
// Regenerate/extend it alongside the sitemap; the research provenance for each
// entry (head term + search volume) is in docs/seo-metadata-research.json.
// -----------------------------------------------------------------------------

import META from "./seo-metadata.json";

/**
 * Strip a pathname down to the key format used in seo-metadata.json: no
 * query/hash, no trailing slash (except the bare "/" home key). Case is
 * PRESERVED — two live routes really are mixed-case (/maharashtra/8A and
 * /maharashtra/eFerfar), so lower-casing here silently missed them.
 */
export const toMetaKey = (pathname) => {
  const raw = (pathname ?? "").toString().trim();
  if (!raw) return "/";
  const trimmed = raw.split("#")[0].split("?")[0].replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
};

// Case-insensitive fallback index, so a link written /Telangana/EC still finds
// its metadata. Built once at module load; the exact-match lookup is tried
// first so the mixed-case routes always win over their lower-cased twin.
const LOWER_INDEX = Object.keys(META).reduce((acc, key) => {
  acc[key.toLowerCase()] = key;
  return acc;
}, {});

/**
 * Curated metadata for a pathname, or null when the route isn't one of the 252.
 * Returning null (rather than a default) is deliberate: dynamic routes that are
 * NOT in the sitemap — /intelligence/atlas/v/:village, /properties/*, /dashboard
 * — must keep computing their own head tags from live data.
 *
 * Returned shape: { title, description, canonical, keywords: string[] }.
 */
export const getRouteMeta = (pathname) => {
  const key = toMetaKey(pathname);
  return META[key] || META[LOWER_INDEX[key.toLowerCase()]] || null;
};

/** Every path covered here, for build scripts (prerender / sitemap parity). */
export const metaRoutes = () => Object.keys(META);

export default META;
